import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  SubmitErrorHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { cloneDeep, get, isEqual, set } from 'lodash';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { CreateLoadRequest } from 'lib/api/createLoad';
import { getCustomerLTLAccessorials } from 'lib/api/getCustomerLTLAccessorials';
import {
  RefNumRequirement,
  getRefNumRequirements,
} from 'lib/api/getRefNumRequirements';
import { ServiceOption } from 'lib/api/getServiceOptions';
import { fetchCustomerByExternalTMSID } from 'lib/api/searchCustomers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import {
  FuelSurchargeCostType,
  LinehaulCostType,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/constants';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { isSuggestedTransportTypeEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  HANDLING_UNIT_TYPES_THREEG,
  LTLAccessorial,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import {
  AccessorialItem,
  AdditionalReference,
  Commodity,
  CostLineItem,
  Load,
  NormalizedLoad,
  Note,
  RateData,
  Specifications,
  TMSCustomer,
  TMSLocation,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { UpdateTMSLoadPayload } from 'types/api/LoadPayload';
import { TMSOperation } from 'types/api/UpdateTMS';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { captureResponseInPosthog } from 'utils/captureResponseInPosthog';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { createLoadDateOnlyMetadata } from 'utils/dateOnlyFormValue';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../../utils/stopValidation';
import {
  AvailableTabs,
  FORM_RESET_OPTIONS,
  MOHAWK_PROD_TMSID,
} from '../constants';
import {
  createFlatRateCostLineItem,
  getPreferredRefrigeratedTransportType,
  hasCustomerDisplayName,
  mergeResolvedCustomerIntoList,
  normalizeServiceType,
  normalizeThreeGCommodityForCreate,
  normalizeThreeGCommodityFromSuggestion,
  normalizeTransportTypeWithSize,
  omitEmptyThreeGLTLDeliveryDate,
  openCloseToHHMM,
} from '../utils/threeGUtils';
import { validateThreeGReferences } from '../validateReferences';
import { useThreeGData } from './useThreeGData';

interface UseThreeGFormLogicParams {
  isCreateMode: boolean;
  load?: Maybe<Load>;
  tmsID?: number;
  threadId?: string;
  emailId?: number;
  threadItemId?: string;
  clickedSuggestion?: Maybe<GenericSuggestion>;
  originalSuggestionData?: NormalizedLoad;
  onSubmit?: (data: NormalizedLoad) => void;
  onInvalid?: SubmitErrorHandler<NormalizedLoad>;
}

type ThreeGCreatePayloadInput = Omit<CreateLoadRequest['load'], 'consignee'> & {
  consignee: Load['consignee'];
};

type ThreeGUpdatePayloadInput = Omit<UpdateTMSLoadPayload, 'consignee'> & {
  consignee: Load['consignee'];
};

export function useThreeGFormLogic({
  isCreateMode,
  load,
  tmsID,
  threadId,
  emailId,
  threadItemId,
  clickedSuggestion,
  originalSuggestionData,
  onSubmit,
  onInvalid,
}: UseThreeGFormLogicParams) {
  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const posthog = usePostHog();
  const { serviceID, serviceFeaturesEnabled } = useServiceFeatures();
  const { setCurrentState, currentState } = useContext(SidebarStateContext);
  const { goToSuggestion } = currentState;
  const tmsName = TMS.ThreeG;

  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showAIHints, setShowAIHints] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<Maybe<NormalizedLoad>>(null);
  const [carrierIsQualified, setCarrierIsQualified] = useState(true);
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);

  // Data Layer: Consume useThreeGData hook for all network states
  // This eliminates duplication and ensures race-condition protection is centralized
  const {
    data: {
      customers: dataCustomers,
      locations: dataLocations,
      transportTypes: dataTransportTypes,
      serviceOptions: dataServiceOptions,
    },
    loading: dataLoading,
    actions: {
      setCustomers: setDataCustomers,
      setLocations: setDataLocations, // Used for updating locations (e.g., when user creates new location)
      fetchCustomers: fetchDataCustomers,
      fetchLocations: fetchDataLocations,
      fetchTransportTypes: fetchDataTransportTypes,
      fetchServiceOptions: fetchDataServiceOptions,
    },
  } = useThreeGData(tmsID);

  // Local state for orchestration (refNumRequirements, form-specific loading)
  const [refNumRequirements, setRefNumRequirements] =
    useState<Maybe<RefNumRequirement[]>>(null);
  const [ltlAccessorials, setLtlAccessorials] = useState<LTLAccessorial[]>([]);

  const [loading, setLoading] = useState({
    submitting: false,
    accessorials: false,
  });

  // Merge dataLoading from useThreeGData with local loading state
  const isLoading = useMemo(
    () => ({
      customers: dataLoading.customers,
      locations: dataLoading.locations,
      transportTypes: dataLoading.transportTypes,
      serviceOptions: dataLoading.serviceOptions,
      submitting: loading.submitting,
      accessorials: loading.accessorials,
    }),
    [dataLoading, loading.accessorials, loading.submitting]
  );

  // Expose data layer states with local aliases for backward compatibility
  const customers = dataCustomers;
  const transportTypes = dataTransportTypes;
  const serviceOptions = dataServiceOptions;
  const setCustomers = setDataCustomers;

  // Race condition protection refs (for refNumRequirements - locations/customers handled by useThreeGData)
  const isMountedRef = useRef(true);
  const refNumRequirementsRequestCounterRef = useRef(0);
  const currentRefNumRequirementsRequestIdRef = useRef<Maybe<number>>(null);
  const prevSuggestionIdRef = useRef<Undef<string | number>>(undefined);
  const skipFormPersistRef = useRef(false);
  const suppressDefaultsResetRef = useRef(false);
  const threadSwitchRestoredForThreadIdRef = useRef<Undef<string>>(undefined);
  const threadSwitchGenerationRef = useRef(0);
  const threadSwitchUnsuppressTimeoutRef =
    useRef<Undef<ReturnType<typeof setTimeout>>>(undefined);
  const restoredDraftSuggestionRef = useRef<Maybe<GenericSuggestion>>(null);
  const sawClearedSuggestionAfterRestoreRef = useRef(false);
  const customerResolveRequestRef = useRef<
    Maybe<{
      tmsID: number;
      externalTMSID: string;
    }>
  >(null);
  const customerResolveResultRef = useRef<
    Maybe<{
      tmsID: number;
      externalTMSID: string;
      customer: Maybe<TMSCustomer>;
    }>
  >(null);
  const savedStateRef = useRef<
    Undef<{
      threadID: string;
      values: NormalizedLoad;
      clickedSuggestion: any;
      dirtyFields: any;
    }>
  >(undefined);
  const accessorialSelectionCustomerRef = useRef<Maybe<string>>(null);
  // Ref to track previous mergedLocations result to prevent unnecessary re-renders
  const prevMergedLocationsRef = useRef<TMSLocation[]>([]);

  // Helper functions for form initialization
  const initLoadBuildingForm = useCallback(
    (tmsName: string): NormalizedLoad => {
      return normalizeLoad(tmsName, {
        ...createInitLoad(),
        mode: 'TL',
        specifications: {
          ...createInitSpecs(),
          transportType: '', // Will be set to first available transport type when API loads
          serviceType: '',
        },
        rateData: {
          ...createInitRateData(),
        },
      });
    },
    []
  );

  // Wrapper functions that pass current state to pure utility functions
  const normalizeTransportTypeWithSizeWrapper = useCallback(
    (transportType: Undef<string>, transportSize: Undef<string>): string => {
      return normalizeTransportTypeWithSize(
        transportType,
        transportSize,
        transportTypes
      );
    },
    [transportTypes]
  );

  const normalizeServiceTypeWrapper = useCallback(
    (serviceType: Undef<string>): string => {
      return normalizeServiceType(serviceType, serviceOptions);
    },
    [serviceOptions]
  );

  // Helper function to get default service option based on mode and TMSID
  const getDefaultServiceOption = useCallback(
    (mode: Undef<string>): Undef<string> => {
      if (!serviceOptions || serviceOptions.length === 0) {
        return undefined;
      }

      const isMohawkGlobal = tmsID === MOHAWK_PROD_TMSID;

      if (isMohawkGlobal) {
        // Drumkit's mode is TL; "FTL" is how 3G service options spell it (e.g. "FTL (Dedicated)").
        if (mode === 'TL' || mode === 'FTL') {
          const ftlDedicated = serviceOptions.find(
            (option: ServiceOption) =>
              option.name?.toLowerCase().includes('ftl') &&
              option.name?.toLowerCase().includes('dedicated')
          );
          return ftlDedicated?.id || serviceOptions[0]?.id;
        } else if (mode === 'LTL') {
          const ltl = serviceOptions.find(
            (option: ServiceOption) =>
              option.name?.toLowerCase() === 'ltl' ||
              (option.name?.toLowerCase().includes('ltl') &&
                !option.name?.toLowerCase().includes('ftl'))
          );
          return ltl?.id || serviceOptions[0]?.id;
        }
      }

      return serviceOptions[0]?.id;
    },
    [serviceOptions, tmsID]
  );

  const createModeSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;
  const createModeSuggestionPipeline = isCreateMode
    ? (clickedSuggestion?.pipeline ?? null)
    : null;

  // Read the suggestion payload through a ref so everything derived from it is
  // only re-derived when the create-mode suggestion id changes, never on the
  // transient clickedSuggestion identity churn from extension messages.
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  // Memoized default values with suggestion support
  const prevMemoizedDefaultValuesRef = useRef<Maybe<NormalizedLoad>>(null);
  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    if (builtLoad) {
      const builtResult = normalizeLoad(TMS.ThreeG, builtLoad);
      prevMemoizedDefaultValuesRef.current = builtResult;
      return builtResult;
    }

    const activeSuggestion =
      createModeSuggestionId == null ? null : clickedSuggestionRef.current;
    if (
      isCreateMode &&
      activeSuggestion &&
      createModeSuggestionPipeline === SuggestionPipelines.LoadBuilding
    ) {
      const castedClickedSuggestion =
        activeSuggestion.suggested as SuggestedLoad;
      const transportType =
        castedClickedSuggestion.specifications?.transportType;
      const transportSize =
        castedClickedSuggestion.specifications?.transportSize;
      const serviceType = castedClickedSuggestion.specifications?.serviceType;

      // Only set equipment to Refrigerated if extraction explicitly has temperature-controlled requirements
      const normalizedTransport = normalizeTransportTypeWithSizeWrapper(
        transportType,
        transportSize
      );
      const specs = castedClickedSuggestion.specifications;
      const hasExplicitTemperature =
        specs?.minTempFahrenheit != null || specs?.maxTempFahrenheit != null;
      const isRefrigeratedType = (t: string) => {
        const lower = t?.toLowerCase() ?? '';
        return lower.includes('refrigerat') || lower.includes('reefer');
      };
      let finalTransportType: string;
      if (isSuggestedTransportTypeEmpty(specs)) {
        // Email named no equipment: leave it empty for the rep to pick, even
        // when a trailer length alone would match an option.
        finalTransportType = '';
      } else if (
        isRefrigeratedType(normalizedTransport) &&
        !hasExplicitTemperature
      ) {
        finalTransportType = '';
      } else if (
        hasExplicitTemperature &&
        !isRefrigeratedType(normalizedTransport)
      ) {
        // Extraction has temperature (e.g. minTemp 32) but transportType is generic (e.g. "TL") — use refrigerated equipment
        finalTransportType =
          getPreferredRefrigeratedTransportType(transportTypes) ||
          normalizedTransport;
      } else {
        finalTransportType = normalizedTransport;
      }

      const carrierLineItemsArray: CostLineItem[] =
        normalizeToArray(castedClickedSuggestion.rateData?.carrierLineItems) ||
        [];

      const commoditiesArray: Commodity[] = (
        normalizeToArray(castedClickedSuggestion.commodities) || []
      ).map(normalizeThreeGCommodityFromSuggestion);
      const additionalReferencesArray: AdditionalReference[] =
        normalizeToArray(castedClickedSuggestion.additionalReferences) || [];
      const notesArray: Note[] =
        normalizeToArray(castedClickedSuggestion.notes) || [];

      // Create a deep copy of rateData to avoid mutating shared state
      // This is critical because castedClickedSuggestion is a type cast of shared context state
      const rateDataCopy = cloneDeep(castedClickedSuggestion.rateData);

      let linehaulCharge: Maybe<CostLineItem> = null;
      let fscLineItem: Maybe<CostLineItem> = null;

      if (
        castedClickedSuggestion.rateData.customerLineHaulCharge &&
        castedClickedSuggestion.rateData.customerLineHaulCharge.val > 0
      ) {
        linehaulCharge = createFlatRateCostLineItem(
          LinehaulCostType,
          castedClickedSuggestion.rateData.customerLineHaulCharge.val
        );

        // Mutate the copy, not the original shared state
        if (rateDataCopy.customerLineHaulCharge) {
          rateDataCopy.customerLineHaulCharge.val = 0;
        }
        rateDataCopy.customerLineHaulRate = null;
      }

      if (
        castedClickedSuggestion.rateData.fscFlatRate &&
        castedClickedSuggestion.rateData.fscFlatRate > 0
      ) {
        fscLineItem = createFlatRateCostLineItem(
          FuelSurchargeCostType,
          castedClickedSuggestion.rateData.fscFlatRate
        );

        // Mutate the copy, not the original shared state
        rateDataCopy.fscFlatRate = 0;
      }

      const suggestedFields = {
        ...castedClickedSuggestion,
        mode: 'TL',
        commodities: commoditiesArray,
        additionalReferences: additionalReferencesArray,
        notes: notesArray,
        customer: {
          ...castedClickedSuggestion.customer,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.customer?.refNumberCandidates
          ),
        },
        pickup: {
          ...castedClickedSuggestion.pickup,
          // 3G uses HH:MM for dock hours; suggestion may have Date (or Invalid Date) from normalizer
          openTime: openCloseToHHMM(castedClickedSuggestion.pickup?.openTime),
          closeTime: openCloseToHHMM(castedClickedSuggestion.pickup?.closeTime),
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.pickup?.refNumberCandidates
          ),
        },
        consignee: {
          ...castedClickedSuggestion.consignee,
          openTime: openCloseToHHMM(
            castedClickedSuggestion.consignee?.openTime
          ),
          closeTime: openCloseToHHMM(
            castedClickedSuggestion.consignee?.closeTime
          ),
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.consignee?.refNumberCandidates
          ),
        },
        specifications: {
          ...createInitSpecs(),
          ...castedClickedSuggestion.specifications,
          transportType: finalTransportType,
          serviceType:
            serviceOptions && serviceOptions.length > 0 && serviceType
              ? normalizeServiceTypeWrapper(serviceType) || serviceType || ''
              : serviceType || '',
        } as Specifications,
        rateData: {
          ...createInitRateData(),
          ...rateDataCopy,
          customerTotalCharge: {
            ...rateDataCopy.customerTotalCharge,
            unit: rateDataCopy.customerTotalCharge?.unit ?? Unit.USD,
          },
          carrierTotalCost: {
            ...rateDataCopy.carrierTotalCost,
            unit: rateDataCopy.carrierTotalCost?.unit ?? Unit.USD,
          },
          customerLineItems: [linehaulCharge, fscLineItem].filter(
            Boolean
          ) as CostLineItem[],
          carrierLineItems: carrierLineItemsArray,
        } as RateData,
      };

      const res = {
        ...normalizeLoad(TMS.ThreeG, createInitLoad()),
        ...suggestedFields,
        rateData: {
          ...suggestedFields.rateData,
          customerTotalCharge: {
            ...suggestedFields.rateData.customerTotalCharge,
            unit: Unit.USD,
          },
        },
      } as NormalizedLoad;

      prevMemoizedDefaultValuesRef.current = res;
      return res;
    }

    if (!isCreateMode && load) {
      const normalizedLoad = normalizeLoad(TMS.ThreeG, load);

      if (
        normalizedLoad.specifications.serviceType &&
        serviceOptions &&
        serviceOptions.length > 0
      ) {
        const currentServiceType = normalizedLoad.specifications.serviceType;
        const isAlreadyId = serviceOptions.some(
          (option) => option.id === currentServiceType
        );

        if (!isAlreadyId) {
          const matchingOption = serviceOptions.find(
            (option) => option.name === currentServiceType
          );
          if (matchingOption) {
            normalizedLoad.specifications.serviceType = matchingOption.id;
          }
        }
      }

      if (
        normalizedLoad.specifications.transportType &&
        transportTypes &&
        transportTypes.length > 0
      ) {
        const currentTransportType =
          normalizedLoad.specifications.transportType;
        const isAlreadyValue = transportTypes.some(
          (type) => type.value === currentTransportType
        );

        if (!isAlreadyValue) {
          const matchingType = transportTypes.find(
            (type) => type.label === currentTransportType
          );
          if (matchingType) {
            normalizedLoad.specifications.transportType =
              matchingType.value || matchingType.label;
          }
        }
      }

      prevMemoizedDefaultValuesRef.current = normalizedLoad;
      return normalizedLoad;
    }

    const result = initLoadBuildingForm(TMS.ThreeG);
    prevMemoizedDefaultValuesRef.current = result;
    return result;
    // Reinitialize only for a different suggestion, not refreshed object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCreateMode,
    createModeSuggestionId,
    createModeSuggestionPipeline,
    builtLoad,
    load,
    transportTypes,
    serviceOptions,
    normalizeTransportTypeWithSizeWrapper,
    normalizeServiceTypeWrapper,
    initLoadBuildingForm,
  ]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  // Match rendered accordion sections only (rates is never shown; others are gated).
  const watchedMode = useWatch({
    control: formMethods.control,
    name: 'mode',
  });
  const isLTLMode = watchedMode === 'LTL';
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      ...(isCreateMode ? [] : [AvailableTabs.billTo]),
      AvailableTabs.specifications,
      AvailableTabs.items,
      ...(isLTLMode ? [AvailableTabs.accessorials] : []),
      AvailableTabs.pickup,
      AvailableTabs.consignee,
    ];
    if (!isCreateMode) {
      tabs.push(AvailableTabs.carrier);
      if (serviceFeaturesEnabled.isOperatorEnabled) {
        tabs.push(AvailableTabs.operator);
      }
      tabs.push(AvailableTabs.documents);
    }
    return tabs;
  }, [isCreateMode, isLTLMode, serviceFeaturesEnabled.isOperatorEnabled]);
  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });

  // Watch full objects for injection logic (but don't use in dependencies)
  const watchedPickup = useWatch({
    control: formMethods.control,
    name: 'pickup',
  });
  const watchedConsignee = useWatch({
    control: formMethods.control,
    name: 'consignee',
  });

  // Watch only IDs for stable dependencies (prevents recalculation on address field changes)
  const watchedPickupID = watchedPickup?.externalTMSID;
  const watchedConsigneeID = watchedConsignee?.externalTMSID;

  // Customer from form (suggestion or search). 3G locations are per-customer; we only ever use external TMS id for locations.
  const customerExternalTmsId = useWatch({
    control: formMethods.control,
    name: 'customer.externalTMSID',
  }) as string | undefined;

  const trimmedCustomerExternalTmsId =
    typeof customerExternalTmsId === 'string'
      ? customerExternalTmsId.trim()
      : '';

  const selectedCustomer = customers?.find(
    (c) =>
      c.externalTMSID != null &&
      String(c.externalTMSID).trim() === trimmedCustomerExternalTmsId
  );
  const customerAccessorialExternalId =
    selectedCustomer != null
      ? String(
          (selectedCustomer as { externalId?: string; externalID?: string })
            .externalId ??
            (selectedCustomer as { externalId?: string; externalID?: string })
              .externalID ??
            ''
        ).trim()
      : '';
  const customerID =
    selectedCustomer != null
      ? ((selectedCustomer as { ID?: number; id?: number }).ID ??
        (selectedCustomer as { ID?: number; id?: number }).id)
      : undefined;
  const hasValidCustomerID =
    typeof customerID === 'number' && Number.isFinite(customerID);

  useEffect(() => {
    if (!tmsID || !trimmedCustomerExternalTmsId) {
      customerResolveRequestRef.current = null;
      customerResolveResultRef.current = null;
      return undefined;
    }

    if (hasCustomerDisplayName(selectedCustomer)) {
      const cachedResult = customerResolveResultRef.current;
      const cachedResultMatchesCurrentCustomer =
        cachedResult?.tmsID === tmsID &&
        cachedResult.externalTMSID === trimmedCustomerExternalTmsId;

      if (!cachedResultMatchesCurrentCustomer) {
        customerResolveResultRef.current = null;
      } else {
        customerResolveResultRef.current = {
          tmsID,
          externalTMSID: trimmedCustomerExternalTmsId,
          customer: selectedCustomer ?? null,
        };
      }

      customerResolveRequestRef.current = null;
      return undefined;
    }

    const previousResolveResult = customerResolveResultRef.current;
    const previousResolveMatchesCurrentCustomer =
      previousResolveResult?.tmsID === tmsID &&
      previousResolveResult.externalTMSID === trimmedCustomerExternalTmsId;

    if (
      previousResolveMatchesCurrentCustomer &&
      previousResolveResult.customer != null
    ) {
      const previouslyResolvedCustomer = previousResolveResult.customer;

      if (!hasCustomerDisplayName(previouslyResolvedCustomer)) {
        return undefined;
      }

      setCustomers((prevCustomers) =>
        mergeResolvedCustomerIntoList(
          previouslyResolvedCustomer,
          prevCustomers ?? []
        )
      );
      return undefined;
    }

    if (previousResolveMatchesCurrentCustomer) {
      return undefined;
    }

    const inFlightRequest = customerResolveRequestRef.current;
    if (
      inFlightRequest?.tmsID === tmsID &&
      inFlightRequest.externalTMSID === trimmedCustomerExternalTmsId
    ) {
      return undefined;
    }

    let isCancelled = false;
    customerResolveRequestRef.current = {
      tmsID,
      externalTMSID: trimmedCustomerExternalTmsId,
    };

    void fetchCustomerByExternalTMSID(tmsID, trimmedCustomerExternalTmsId).then(
      (res) => {
        if (isCancelled) {
          return;
        }

        customerResolveRequestRef.current = null;
        if (res.isErr() || !res.value?.externalTMSID) {
          customerResolveResultRef.current = {
            tmsID,
            externalTMSID: trimmedCustomerExternalTmsId,
            customer: null,
          };
          return;
        }

        const resolvedCustomer = res.value;
        customerResolveResultRef.current = {
          tmsID,
          externalTMSID: trimmedCustomerExternalTmsId,
          customer: resolvedCustomer,
        };
        setCustomers((prevCustomers) =>
          mergeResolvedCustomerIntoList(resolvedCustomer, prevCustomers ?? [])
        );
      }
    );

    return () => {
      isCancelled = true;
      if (
        customerResolveRequestRef.current?.tmsID === tmsID &&
        customerResolveRequestRef.current.externalTMSID ===
          trimmedCustomerExternalTmsId
      ) {
        customerResolveRequestRef.current = null;
      }
    };
  }, [tmsID, trimmedCustomerExternalTmsId, selectedCustomer, setCustomers]);

  // Merge API locations, suggested locations, and current form selections.
  // Keep suggestions first so API refreshes do not erase them.
  const mergedLocations = useMemo(() => {
    // Use dataLocations directly - no intermediate state needed
    let base = [...(dataLocations ?? [])];

    // CRITICAL: Inject AI-suggested locations FIRST (from originalSuggestionData)
    // This ensures they are never overwritten by API refresh
    if (originalSuggestionData?.pickup?.externalTMSID?.trim()) {
      const suggestedPickup = originalSuggestionData.pickup as TMSLocation;
      if (
        suggestedPickup?.addressLine1 ||
        suggestedPickup?.name ||
        suggestedPickup?.city
      ) {
        base = injectSelectedObject(suggestedPickup, base);
      }
    }

    if (originalSuggestionData?.consignee?.externalTMSID?.trim()) {
      const suggestedConsignee =
        originalSuggestionData.consignee as TMSLocation;
      if (
        suggestedConsignee?.addressLine1 ||
        suggestedConsignee?.name ||
        suggestedConsignee?.city
      ) {
        base = injectSelectedObject(suggestedConsignee, base);
      }
    }

    // Then inject current form values to ensure selects can resolve
    if (watchedPickup?.externalTMSID?.trim()) {
      const formPickup = watchedPickup as TMSLocation;
      if (formPickup?.addressLine1 || formPickup?.name || formPickup?.city) {
        base = injectSelectedObject(formPickup, base);
      }
    }

    if (watchedConsignee?.externalTMSID?.trim()) {
      const formConsignee = watchedConsignee as TMSLocation;
      if (
        formConsignee?.addressLine1 ||
        formConsignee?.name ||
        formConsignee?.city
      ) {
        base = injectSelectedObject(formConsignee, base);
      }
    }

    // CRITICAL: Compare with previous result to prevent unnecessary re-renders
    // If content is identical, return previous reference to maintain referential equality
    const isContentEqual = isEqual(base, prevMergedLocationsRef.current);
    if (isContentEqual) {
      return prevMergedLocationsRef.current;
    }

    // Update ref with new result
    prevMergedLocationsRef.current = base;
    return base;
    // Only selection IDs trigger rebuilding the list, not edits to address fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataLocations,
    watchedPickupID,
    watchedConsigneeID,
    originalSuggestionData?.pickup?.externalTMSID,
    originalSuggestionData?.consignee?.externalTMSID,
  ]);

  // Form state persistence for create mode - includes loading saved state.
  // Held in a latest-ref so the effect below keeps its narrow trigger list:
  // pulling `formMethods`, `goToSuggestion` or the data setters into the effect
  // deps re-subscribes the form watcher on every render and loops.
  const persistFormStateRef = useRef<
    (params: {
      threadId: typeof threadId;
      tmsName: typeof tmsName;
      clickedSuggestion: typeof clickedSuggestion;
      isLoadBuildingSuggestionClicked: boolean;
    }) => Undef<() => void>
  >(() => undefined);
  persistFormStateRef.current = ({
    threadId,
    tmsName,
    clickedSuggestion,
    isLoadBuildingSuggestionClicked,
  }) => {
    if (isCreateMode && threadId) {
      const savedState = FormStorageService.getFormState<
        NormalizedLoad,
        NormalizedLoad
      >(`${tmsName}_${threadId}`);

      // Store savedState in ref for use in watch callback
      savedStateRef.current = savedState;

      if (
        savedState &&
        savedState.threadID === threadId &&
        clickedSuggestion == null &&
        threadSwitchRestoredForThreadIdRef.current !== threadId
      ) {
        setShowAIHints(true);
        if (goToSuggestion) {
          goToSuggestion({
            suggestionID: savedState.clickedSuggestion?.id,
          });
        }

        // Flatten savedState.values to get all field names
        const fieldNames = flattenValues(savedState.values);
        const cleanFields = normalizeLoad(tmsName, createInitLoad());

        // For each field, set the value and mark as dirty if necessary
        fieldNames.forEach((fieldName) => {
          const isDirty: Undef<boolean> = get(
            savedState.dirtyFields,
            fieldName
          );
          if (!isDirty) {
            set(cleanFields, fieldName, get(savedState.values, fieldName));
          }
        });

        formMethods.reset(cleanFields as NormalizedLoad, FORM_RESET_OPTIONS);

        setTimeout(() => {
          fieldNames.forEach((fieldName) => {
            const isDirty: Undef<boolean> = get(
              savedState.dirtyFields,
              fieldName
            );

            if (isDirty) {
              formMethods.setValue(
                fieldName as FieldPath<NormalizedLoad>,
                get(savedState.values, fieldName),
                {
                  shouldDirty: isDirty,
                  shouldTouch: true,
                }
              );
            }
          });

          // After resetting the form, ensure that options arrays include the necessary data
          const savedCustomerExternalId =
            savedState.values.customer?.externalTMSID;
          if (savedCustomerExternalId) {
            setCustomers((prevCustomers) =>
              injectSelectedObject(
                savedState.values.customer,
                prevCustomers ?? []
              )
            );
          }

          const additionalLocations: TMSLocation[] = [];
          if (savedState.values.pickup?.externalTMSID) {
            additionalLocations.push(savedState.values.pickup);
          }

          if (savedState.values.consignee?.externalTMSID) {
            additionalLocations.push(savedState.values.consignee);
          }

          // Update dataLocations with saved form locations
          // These locations will be merged into mergedLocations via useMemo
          // Only update if locations actually need to be added (prevents unnecessary re-renders)
          setDataLocations((prevLocations) => {
            let updatedLocations = prevLocations ?? [];
            let hasChanges = false;

            additionalLocations.forEach((loc) => {
              const beforeLength = updatedLocations.length;
              updatedLocations = injectSelectedObject(loc, updatedLocations);
              if (updatedLocations.length !== beforeLength) {
                hasChanges = true;
              }
            });

            // Return previous reference if no changes to prevent unnecessary re-renders
            return hasChanges ? updatedLocations : (prevLocations ?? []);
          });
        }, 100);
      }

      // Wait for RHF to finish rendering before caching initial form state
      setTimeout(() => {
        if (skipFormPersistRef.current) {
          return;
        }
        if (!savedState || threadId === savedState.threadID) {
          FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
            threadID: threadId,
            values: formMethods.getValues(),
            clickedSuggestion:
              (isLoadBuildingSuggestionClicked && clickedSuggestion) ||
              restoredDraftSuggestionRef.current,
            dirtyFields: formMethods.formState.dirtyFields,
          });
        }
      }, 100);

      // Subscribe to form to cache state on changes (debounced)
      let timeoutId: NodeJS.Timeout;
      const subscription = formMethods.watch(() => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          // CRITICAL: Only access formMethods inside the debounced callback to avoid infinite loops
          if (!threadId || skipFormPersistRef.current) {
            return;
          }
          const savedState = savedStateRef.current;
          if (!savedState || threadId === savedState.threadID) {
            // Capture all form state at the time of save, not during watch callback
            const currentValues = formMethods.getValues();
            const currentDirtyFields = formMethods.formState.dirtyFields;

            FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
              threadID: threadId,
              values: currentValues,
              clickedSuggestion:
                (isLoadBuildingSuggestionClicked && clickedSuggestion) ||
                restoredDraftSuggestionRef.current,
              dirtyFields: currentDirtyFields,
            });
          }
        }, 500); // Debounce to prevent lag
      });

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  };

  const prevThreadIdRef = useRef<Undef<string>>(undefined);
  const resetFormOnThreadSwitchRef = useRef<() => void>(() => {});
  resetFormOnThreadSwitchRef.current = () => {
    if (!isCreateMode || !threadId) {
      return;
    }

    threadSwitchGenerationRef.current += 1;
    const generation = threadSwitchGenerationRef.current;

    if (threadSwitchUnsuppressTimeoutRef.current != null) {
      clearTimeout(threadSwitchUnsuppressTimeoutRef.current);
      threadSwitchUnsuppressTimeoutRef.current = undefined;
    }

    skipFormPersistRef.current = true;
    suppressDefaultsResetRef.current = true;

    setBuiltLoad(null);
    setPendingSubmissionData(null);
    setShowDuplicateWarning(false);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    const savedState = FormStorageService.getFormState<
      NormalizedLoad,
      NormalizedLoad
    >(`${tmsName}_${threadId}`);
    savedStateRef.current = savedState;

    if (savedState && savedState.threadID === threadId && savedState.values) {
      threadSwitchRestoredForThreadIdRef.current = threadId;
      prevSuggestionIdRef.current =
        savedState.clickedSuggestion?.id ?? `thread-switch:${threadId}`;

      const fieldNames = flattenValues(savedState.values);
      const cleanFields = normalizeLoad(tmsName, createInitLoad());

      fieldNames.forEach((fieldName) => {
        set(cleanFields, fieldName, get(savedState.values, fieldName));
      });

      formMethods.reset(cleanFields as NormalizedLoad, FORM_RESET_OPTIONS);
      formMethods.clearErrors();
      restoredDraftSuggestionRef.current =
        (savedState.clickedSuggestion as Maybe<GenericSuggestion>) ?? null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      const hadSuggestion = savedState.clickedSuggestion?.id != null;
      setIsLoadBuildingSuggestionClicked(hadSuggestion);
      setShowAIHints(hadSuggestion);

      fieldNames.forEach((fieldName) => {
        const isDirty: Undef<boolean> = get(savedState.dirtyFields, fieldName);
        if (isDirty) {
          formMethods.setValue(
            fieldName as FieldPath<NormalizedLoad>,
            get(savedState.values, fieldName),
            {
              shouldDirty: true,
              shouldTouch: true,
            }
          );
        }
      });
    } else {
      threadSwitchRestoredForThreadIdRef.current = undefined;
      restoredDraftSuggestionRef.current = null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      prevSuggestionIdRef.current = `thread-switch:${threadId}`;
      setIsLoadBuildingSuggestionClicked(false);
      setShowAIHints(false);
      formMethods.reset(initLoadBuildingForm(tmsName), FORM_RESET_OPTIONS);
      formMethods.clearErrors();
    }

    threadSwitchUnsuppressTimeoutRef.current = setTimeout(() => {
      if (generation !== threadSwitchGenerationRef.current) {
        return;
      }
      skipFormPersistRef.current = false;
      suppressDefaultsResetRef.current = false;
      threadSwitchUnsuppressTimeoutRef.current = undefined;
    }, 150);
  };

  useEffect(() => {
    if (!threadId) {
      return;
    }
    const previousThreadId = prevThreadIdRef.current;
    prevThreadIdRef.current = threadId;

    if (previousThreadId == null || previousThreadId === threadId) {
      return;
    }
    resetFormOnThreadSwitchRef.current();
  }, [threadId]);

  useEffect(() => {
    return () => {
      if (threadSwitchUnsuppressTimeoutRef.current != null) {
        clearTimeout(threadSwitchUnsuppressTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCreateMode) {
      return undefined;
    }
    return persistFormStateRef.current({
      threadId,
      tmsName,
      clickedSuggestion,
      isLoadBuildingSuggestionClicked,
    });
  }, [
    isCreateMode,
    threadId,
    tmsName,
    clickedSuggestion,
    isLoadBuildingSuggestionClicked,
  ]);

  // Ref num: same pattern as getLocations — pass customerExternalTmsId or customerID (at most one).
  // The external ID stays stable when the customer list later resolves a DB ID.
  const requirementsKey = `${tmsID}:${trimmedCustomerExternalTmsId || `id:${customerID}`}`;
  const loadedRequirementsKeyRef = useRef<string | null>(null);
  const [requirementsState, setRequirementsState] = useState<{
    key: string;
    status: 'loading' | 'loaded' | 'failed';
  }>({ key: '', status: 'loading' });

  const fetchRefNumRequirements = useCallback(async () => {
    if (loadedRequirementsKeyRef.current === requirementsKey) return;
    loadedRequirementsKeyRef.current = null;
    const requestId = ++refNumRequirementsRequestCounterRef.current;
    currentRefNumRequirementsRequestIdRef.current = requestId;
    setRefNumRequirements(null);
    setRequirementsState({ key: requirementsKey, status: 'loading' });
    const hasCustomer =
      (hasValidCustomerID && customerID != null) ||
      trimmedCustomerExternalTmsId !== '';
    if (!tmsID || !hasCustomer) {
      setRefNumRequirements(null);
      return;
    }

    const res = await getRefNumRequirements(
      tmsID,
      hasValidCustomerID ? undefined : trimmedCustomerExternalTmsId,
      hasValidCustomerID ? (customerID as number) : undefined
    );

    if (!isMountedRef.current) {
      return;
    }

    if (currentRefNumRequirementsRequestIdRef.current !== requestId) {
      return;
    }

    if (res.isOk()) {
      const requirements = res.value.requirements ?? [];
      loadedRequirementsKeyRef.current = requirementsKey;
      setRefNumRequirements(requirements);
      setRequirementsState({ key: requirementsKey, status: 'loaded' });
      return;
    }
    setRefNumRequirements(null);
    setRequirementsState({ key: requirementsKey, status: 'failed' });
  }, [
    tmsID,
    hasValidCustomerID,
    customerID,
    trimmedCustomerExternalTmsId,
    requirementsKey,
  ]);

  // Locations: 3G form only passes external TMS id when a customer is selected (filtered). Otherwise unfiltered.
  const fetchLocations = useCallback(
    async (forceRefresh = false) => {
      await fetchDataLocations(
        forceRefresh,
        trimmedCustomerExternalTmsId || undefined
      );
    },
    [fetchDataLocations, trimmedCustomerExternalTmsId]
  );

  const refreshLocations = useCallback(async () => {
    // Refresh incrementally; customers without local locations get a full sync.
    await fetchDataLocations(true, trimmedCustomerExternalTmsId || undefined);
  }, [fetchDataLocations, trimmedCustomerExternalTmsId]);

  const refreshTransportTypes = useCallback(async () => {
    await fetchDataTransportTypes(true);
  }, [fetchDataTransportTypes]);

  const refreshServiceOptions = useCallback(async () => {
    await fetchDataServiceOptions(true);
  }, [fetchDataServiceOptions]);

  // Helper function for error toasts
  const showErrorToast = useCallback(
    (description: string) => {
      toast({
        description,
        variant: 'destructive',
      });
    },
    [toast]
  );

  // Track mounted state for race-condition protection
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize data on mount - delegate to useThreeGData
  useEffect(() => {
    if (!tmsID) return;

    fetchDataCustomers();
    fetchDataTransportTypes(false);
    fetchDataServiceOptions(false);
  }, [
    tmsID,
    fetchDataCustomers,
    fetchDataTransportTypes,
    fetchDataServiceOptions,
  ]);

  // Fetch locations: when a customer is selected we filter by that customer (external TMS id). Otherwise unfiltered.
  useEffect(() => {
    if (!tmsID) return;
    // Drop stale rows immediately so mergedLocations + location select cannot briefly keep an
    // unscoped list next to the new customer (RHFLocationSelect baseline would capture that full list).
    setDataLocations([]);
    fetchLocations(false);
  }, [tmsID, trimmedCustomerExternalTmsId, fetchLocations, setDataLocations]);

  // Refetch reference number requirements when customer selection changes (customerID or externalTMSID)
  useEffect(() => {
    const hasCustomer =
      (hasValidCustomerID && customerID != null) ||
      trimmedCustomerExternalTmsId !== '';
    if (tmsID && hasCustomer) {
      fetchRefNumRequirements();
    } else {
      loadedRequirementsKeyRef.current = null;
      setRefNumRequirements(null);
      currentRefNumRequirementsRequestIdRef.current = null;
    }
  }, [
    tmsID,
    hasValidCustomerID,
    customerID,
    trimmedCustomerExternalTmsId,
    fetchRefNumRequirements,
  ]);

  // Handle showAIHints state updates.
  // After thread-switch draft restore, clickedSuggestion is still the previous
  // thread's value for this commit — do not clear restoredDraftSuggestionRef.
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (threadSwitchRestoredForThreadIdRef.current === threadId) {
      if (!clickedSuggestion) {
        sawClearedSuggestionAfterRestoreRef.current = true;
        return;
      }
      if (!sawClearedSuggestionAfterRestoreRef.current) {
        return;
      }

      threadSwitchRestoredForThreadIdRef.current = undefined;
      restoredDraftSuggestionRef.current = null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      setShowAIHints(true);
      return;
    }

    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      restoredDraftSuggestionRef.current = null;
      setShowAIHints(true);
    } else {
      setShowAIHints(false);
    }
  }, [isCreateMode, clickedSuggestion, threadId]);

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? (clickedSuggestion.id ?? null)
      : null;

  // CRITICAL: Reset form when Load Building suggestion data changes or defaults update.
  // Load View CI/Appt use surgical dirty setValue — do not hard-reset on those clicks.
  useEffect(() => {
    if (suppressDefaultsResetRef.current) {
      if (createModeLoadBuildingSuggestionId != null) {
        prevSuggestionIdRef.current = createModeLoadBuildingSuggestionId;
      }

      return undefined;
    }

    const isNewLoadBuildingSuggestion =
      createModeLoadBuildingSuggestionId != null &&
      String(createModeLoadBuildingSuggestionId) !==
        String(prevSuggestionIdRef.current);
    const isFormDirty = formMethods.formState.isDirty;

    if (!(isNewLoadBuildingSuggestion || !isFormDirty)) {
      return undefined;
    }

    if (createModeLoadBuildingSuggestionId != null) {
      prevSuggestionIdRef.current = createModeLoadBuildingSuggestionId;
    }

    // Check if current serviceType is already set to a mode-based default
    const currentServiceType = formMethods.getValues(
      'specifications.serviceType'
    );
    const currentMode = formMethods.getValues('mode');
    const modeBasedDefault = getDefaultServiceOption(currentMode);
    const shouldPreserveServiceType =
      modeBasedDefault &&
      currentServiceType &&
      String(currentServiceType) === String(modeBasedDefault) &&
      !isNewLoadBuildingSuggestion;

    formMethods.reset(memoizedDefaultValues, FORM_RESET_OPTIONS);

    // Restore mode-based default if it was set before reset
    if (shouldPreserveServiceType && modeBasedDefault) {
      formMethods.setValue('specifications.serviceType', modeBasedDefault, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    // If reset set a name (not ID) and serviceOptions are loaded, normalize immediately
    const timeoutId = setTimeout(() => {
      const afterReset = formMethods.getValues('specifications.serviceType');
      if (
        shouldPreserveServiceType &&
        modeBasedDefault &&
        String(afterReset) === String(modeBasedDefault)
      ) {
        return;
      }
      if (
        serviceOptions &&
        serviceOptions.length > 0 &&
        afterReset &&
        !serviceOptions.some((opt) => String(opt.id) === String(afterReset))
      ) {
        const normalizedId = normalizeServiceTypeWrapper(afterReset);
        if (normalizedId && String(normalizedId) !== String(afterReset)) {
          formMethods.setValue(
            'specifications.serviceType',
            String(normalizedId),
            { shouldDirty: false, shouldTouch: false }
          );
        }
      }
    }, 50);

    // Surface missing required fields after AI hydrate (no submit).
    const clearSuggestionValidation = scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );

    return () => {
      clearTimeout(timeoutId);
      clearSuggestionValidation();
    };
    // Default values already track suggestion changes; avoid resetting on object refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memoizedDefaultValues,
    formMethods,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    serviceOptions,
    getDefaultServiceOption,
    normalizeServiceTypeWrapper,
  ]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion ?? null,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion ?? null,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  // Inject customer from suggestion into customers list
  useEffect(() => {
    if (
      !dataLoading.customers &&
      customers &&
      memoizedDefaultValues?.customer?.externalTMSID
    ) {
      setCustomers((prevCustomers) =>
        injectSelectedObject(
          memoizedDefaultValues.customer,
          prevCustomers ?? []
        )
      );
    }
  }, [
    dataLoading.customers,
    memoizedDefaultValues?.customer,
    customers,
    setCustomers,
  ]);

  // Set flag for suggestion presence
  useEffect(() => {
    const isLoadBuilding =
      clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding;
    setIsLoadBuildingSuggestionClicked(isLoadBuilding);
  }, [clickedSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
  }, [clickedSuggestion?.id]);

  // Set default transport type when transportTypes load (create mode) or convert label to value (edit mode)
  useEffect(() => {
    if (
      !dataLoading.transportTypes &&
      transportTypes &&
      transportTypes.length > 0
    ) {
      const currentTransportType = formMethods.getValues(
        'specifications.transportType'
      );
      const isTransportTypeDirty =
        formMethods.formState.dirtyFields.specifications?.transportType;

      // Skip if user has manually changed the transport type
      if (isTransportTypeDirty) {
        return;
      }

      if (
        isCreateMode &&
        createModeSuggestionPipeline === SuggestionPipelines.LoadBuilding
      ) {
        // Builder suggestion: never fill an empty equipment with the first
        // option. A value the options can't show is cleared so the rep picks.
        const exists = transportTypes.some(
          (type) =>
            type.value === currentTransportType ||
            type.label === currentTransportType
        );
        if (currentTransportType && !exists) {
          formMethods.setValue('specifications.transportType', '', {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      } else if (isCreateMode) {
        // If transportType is empty, set to first available
        if (!currentTransportType) {
          const firstTransportType =
            transportTypes[0].value || transportTypes[0].label;
          if (firstTransportType) {
            formMethods.setValue(
              'specifications.transportType',
              firstTransportType,
              { shouldDirty: false, shouldTouch: false }
            );
          }
        } else {
          // Check if current value needs normalization (label -> value)
          const exists = transportTypes.some(
            (type) =>
              type.value === currentTransportType ||
              type.label === currentTransportType
          );

          // If current value doesn't exist, set to first available
          if (!exists) {
            const firstTransportType =
              transportTypes[0].value || transportTypes[0].label;
            if (firstTransportType) {
              formMethods.setValue(
                'specifications.transportType',
                firstTransportType,
                { shouldDirty: false, shouldTouch: false }
              );
            }
          }
        }
      } else {
        // Edit mode: convert label to value if needed
        if (currentTransportType) {
          const isAlreadyValue = transportTypes.some(
            (type) => type.value === currentTransportType
          );

          if (!isAlreadyValue) {
            // Try to find a match by label and convert to value
            const matchingType = transportTypes.find(
              (type) => type.label === currentTransportType
            );
            if (matchingType) {
              formMethods.setValue(
                'specifications.transportType',
                matchingType.value || matchingType.label,
                { shouldDirty: false, shouldTouch: false }
              );
            }
          }
        }
      }
    }
  }, [
    isCreateMode,
    createModeSuggestionPipeline,
    dataLoading.transportTypes,
    transportTypes,
    formMethods,
  ]);

  // Convert serviceType from name to ID when serviceOptions load
  useEffect(() => {
    if (
      !dataLoading.serviceOptions &&
      serviceOptions &&
      serviceOptions.length > 0
    ) {
      const currentServiceType = formMethods.getValues(
        'specifications.serviceType'
      );

      // Get value from form or from suggestion defaults (suggestions come as names, form stores IDs)
      const valueToUse =
        currentServiceType ||
        memoizedDefaultValues?.specifications?.serviceType;

      // Skip if still empty
      if (!valueToUse) {
        return;
      }

      // Find the matching option - could be ID (from form) or name (from suggestion)
      const normalizeStr = (str: string) =>
        str.toLowerCase().trim().replace(/\s+/g, ' ');
      const normalizedInput = normalizeStr(valueToUse);

      const matchingOption = serviceOptions.find((opt) => {
        // Try ID match first (form stores IDs)
        if (String(opt.id) === String(valueToUse)) {
          return true;
        }
        // Then try normalized name match (suggestions come as names)
        return normalizeStr(opt.name || '') === normalizedInput;
      });

      // If we found a match, use its ID (form always stores IDs)
      if (matchingOption) {
        const serviceTypeId = String(matchingOption.id);
        if (String(currentServiceType) !== serviceTypeId) {
          formMethods.setValue('specifications.serviceType', serviceTypeId, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      } else if (
        !currentServiceType &&
        isCreateMode &&
        tmsID === MOHAWK_PROD_TMSID
      ) {
        // No match found and form is empty - use default for Mohawk
        const defaultServiceType = getDefaultServiceOption('TL');
        if (defaultServiceType) {
          formMethods.setValue(
            'specifications.serviceType',
            defaultServiceType,
            { shouldDirty: false, shouldTouch: false }
          );
        }
      }
    }
  }, [
    dataLoading.serviceOptions,
    serviceOptions,
    formMethods,
    memoizedDefaultValues,
    isCreateMode,
    tmsID,
    getDefaultServiceOption,
  ]);

  // Update service option when mode changes for Mohawk Global (create mode only)
  useEffect(() => {
    const previousCustomerExternalTmsId =
      accessorialSelectionCustomerRef.current;
    const nextCustomerExternalTmsId =
      watchedMode === 'LTL' && trimmedCustomerExternalTmsId
        ? trimmedCustomerExternalTmsId
        : null;
    const shouldClearAccessorials =
      nextCustomerExternalTmsId == null ||
      (previousCustomerExternalTmsId != null &&
        previousCustomerExternalTmsId !== nextCustomerExternalTmsId);

    if (shouldClearAccessorials) {
      formMethods.setValue('accessorials', [], {
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    accessorialSelectionCustomerRef.current =
      nextCustomerExternalTmsId != null ? nextCustomerExternalTmsId : null;
  }, [formMethods, trimmedCustomerExternalTmsId, watchedMode]);

  useEffect(() => {
    if (watchedMode !== 'LTL') {
      setLtlAccessorials([]);
      setLoading((l) => ({ ...l, accessorials: false }));
      return;
    }

    if (!customerAccessorialExternalId) {
      setLtlAccessorials([]);
      setLoading((l) => ({ ...l, accessorials: false }));
      return;
    }

    let cancelled = false;
    const customerExternalTmsId = customerAccessorialExternalId;

    const fetchAccessorials = async () => {
      setLtlAccessorials([]);
      setLoading((l) => ({ ...l, accessorials: true }));

      const result = await getCustomerLTLAccessorials({
        customerExternalTmsId,
      });

      if (cancelled) {
        return;
      }

      if (result.isOk()) {
        const nextAccessorials = result.value;
        setLtlAccessorials(nextAccessorials);

        const validAccessorialIds = new Set(
          nextAccessorials
            .map((accessorial) =>
              accessorial.externalId != null
                ? String(accessorial.externalId)
                : ''
            )
            .filter(Boolean)
        );
        const currentAccessorials = formMethods.getValues('accessorials') ?? [];
        const validSelectedAccessorials = currentAccessorials.filter(
          (accessorial: AccessorialItem) =>
            accessorial.id != null &&
            validAccessorialIds.has(String(accessorial.id))
        );

        if (validSelectedAccessorials.length !== currentAccessorials.length) {
          formMethods.setValue('accessorials', validSelectedAccessorials, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      } else {
        formMethods.setValue('accessorials', [], {
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      setLoading((l) => ({ ...l, accessorials: false }));
    };

    void fetchAccessorials();

    return () => {
      cancelled = true;
      setLtlAccessorials([]);
      setLoading((l) => ({ ...l, accessorials: false }));
    };
  }, [customerAccessorialExternalId, formMethods, watchedMode]);

  useEffect(() => {
    if (
      isCreateMode &&
      !dataLoading.serviceOptions &&
      serviceOptions &&
      serviceOptions.length > 0 &&
      watchedMode
    ) {
      const currentServiceType = formMethods.getValues(
        'specifications.serviceType'
      );
      const isServiceTypeDirty =
        formMethods.formState.dirtyFields.specifications?.serviceType;

      // Only update if service type hasn't been manually changed AND it's empty or invalid
      // Don't overwrite valid service types from suggestions
      if (!isServiceTypeDirty) {
        // Check if current value is a valid service option ID
        const isValidServiceType =
          currentServiceType &&
          serviceOptions.some(
            (option) => String(option.id) === String(currentServiceType)
          );

        // Only set default if current value is empty or invalid
        if (!isValidServiceType) {
          const defaultServiceType = getDefaultServiceOption(watchedMode);
          if (
            defaultServiceType &&
            String(defaultServiceType) !== String(currentServiceType)
          ) {
            formMethods.setValue(
              'specifications.serviceType',
              defaultServiceType,
              {
                shouldDirty: false,
                shouldTouch: false,
              }
            );
          }
        }
      }
    }
  }, [
    isCreateMode,
    dataLoading.serviceOptions,
    serviceOptions,
    watchedMode,
    formMethods,
    getDefaultServiceOption,
  ]);

  // Helper functions for error handling and form utilities
  // Note: This flattens object keys into dot-notation strings (e.g., "customer.name"),
  // not arrays. lodash's _.flatten is for arrays, so it doesn't apply here.
  function flattenValues(
    values: any,
    parentKey = '',
    result: string[] = []
  ): string[] {
    for (const key in values) {
      const value = values[key];
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (!(value && typeof value === 'object' && !Array.isArray(value))) {
        result.push(newKey);
        continue;
      }

      flattenValues(value, newKey, result);
    }
    return result;
  }

  const createSuccessToast = useCallback(
    (description: string) => ({
      description,
      variant: 'success' as const,
    }),
    []
  );

  // Helper function to get analytics config for PostHog tracking
  const getPostHogAnalyticsConfig = useCallback(
    (isCreateMode: boolean) => ({
      serviceID,
      tmsName,
      submissionType: isCreateMode
        ? ButtonNamePosthog.BuildLoad
        : ButtonNamePosthog.UpdateTMS,
    }),
    [serviceID, tmsName]
  );

  const validateAppointmentTimes = useCallback(
    (sectionName: string, apptStartTime: Date, apptEndTime: Date): boolean => {
      if (apptStartTime && apptEndTime && apptStartTime > apptEndTime) {
        showErrorToast(
          `${sectionName} Section: Appointment Start Time cannot be later than Appointment End Time`
        );
        return false;
      }
      return true;
    },
    [showErrorToast]
  );

  const handleClearForm = useCallback(() => {
    setShowAIHints(false);
    setBuiltLoad(null);
    setPendingSubmissionData(null);
    setShowDuplicateWarning(false);
    setIsLoadBuildingSuggestionClicked(false);
    restoredDraftSuggestionRef.current = null;
    threadSwitchRestoredForThreadIdRef.current = undefined;
    sawClearedSuggestionAfterRestoreRef.current = false;

    FormStorageService.clearFormState(`${tmsName}_${threadId}`);

    // Deselect the Kit suggestion
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    // So re-selecting any suggestion (same or different) triggers form reset
    prevSuggestionIdRef.current = undefined;

    // Reset form to empty values directly - bypass all memoization
    const emptyForm = initLoadBuildingForm(tmsName);
    formMethods.reset(emptyForm, FORM_RESET_OPTIONS);

    // Set defaults for required fields if options are already loaded
    // (useEffects won't re-run if dependencies haven't changed)
    if (isCreateMode) {
      if (transportTypes && transportTypes.length > 0) {
        const firstTransportType =
          transportTypes[0].value || transportTypes[0].label;
        if (firstTransportType) {
          formMethods.setValue(
            'specifications.transportType',
            firstTransportType,
            { shouldDirty: false, shouldTouch: false }
          );
        }
      }

      if (serviceOptions && serviceOptions.length > 0) {
        // Use getDefaultServiceOption to get the correct default based on mode and TMSID
        // initLoadBuildingForm sets mode: 'TL', so we use that for the default
        const defaultServiceType = getDefaultServiceOption('TL');
        if (defaultServiceType) {
          formMethods.setValue(
            'specifications.serviceType',
            defaultServiceType,
            {
              shouldDirty: false,
              shouldTouch: false,
            }
          );
        }
      }
    }

    // Clear any validation errors
    formMethods.clearErrors();
  }, [
    getDefaultServiceOption,
    initLoadBuildingForm,
    setShowAIHints,
    setBuiltLoad,
    setPendingSubmissionData,
    setShowDuplicateWarning,
    tmsName,
    threadId,
    setCurrentState,
    formMethods,
    isCreateMode,
    transportTypes,
    serviceOptions,
  ]);

  // Helper function to validate and transform service type and transport type
  const validateAndTransformServiceAndTransport = useCallback(
    (
      data: NormalizedLoad
    ): Maybe<{ serviceTypeName: string; transportTypeLabel: string }> => {
      // Check if still loading first
      if (dataLoading.serviceOptions) {
        showErrorToast(
          'Service options are still loading. Please wait and try again.'
        );
        setLoading((l) => ({ ...l, submitting: false }));
        return null;
      }

      // Check if loaded but empty
      if (!serviceOptions || serviceOptions.length === 0) {
        showErrorToast(
          'Service options are required but none are available. Please refresh service options and try again.'
        );
        setLoading((l) => ({ ...l, submitting: false }));
        return null;
      }

      // Check if still loading first
      if (dataLoading.transportTypes) {
        showErrorToast(
          'Transport types are still loading. Please wait and try again.'
        );
        setLoading((l) => ({ ...l, submitting: false }));
        return null;
      }

      // Check if loaded but empty
      if (!transportTypes || transportTypes.length === 0) {
        showErrorToast(
          'Transport types are required but none are available. Please refresh transport types and try again.'
        );
        setLoading((l) => ({ ...l, submitting: false }));
        return null;
      }

      // Transform serviceType from ID to name
      const serviceTypeOption =
        serviceOptions.find(
          (option) =>
            String(option.id) === String(data.specifications.serviceType)
        ) ||
        serviceOptions.find(
          (option) =>
            String(option.name) === String(data.specifications.serviceType)
        );

      // Validate that serviceType is required and matches a valid option
      if (!serviceTypeOption) {
        if (data.specifications.serviceType) {
          // ServiceType has a value but doesn't match any option
          showErrorToast(
            'Selected service type is no longer valid. Please refresh service options and select again.'
          );
        } else {
          // ServiceType is empty but is required
          showErrorToast(
            'Service type is required. Please select a valid service option.'
          );
        }
        setLoading((l) => ({ ...l, submitting: false }));
        return null;
      }

      const serviceTypeName = serviceTypeOption.name;

      // Transform transportType from value to label
      // The form may store either value or label, so we check both
      let transportTypeLabel = data.specifications.transportType;
      if (transportTypes && data.specifications.transportType) {
        const transportType = transportTypes.find(
          (type) =>
            type.value === data.specifications.transportType ||
            type.label === data.specifications.transportType
        );

        // Validate that the selected transportType exists in options
        if (!transportType && data.specifications.transportType) {
          showErrorToast(
            'Selected transport type is no longer valid. Please refresh transport types and select again.'
          );
          setLoading((l) => ({ ...l, submitting: false }));
          return null;
        }

        if (transportType) {
          transportTypeLabel = transportType.label;
        }
      }

      return { serviceTypeName, transportTypeLabel };
    },
    [
      dataLoading.serviceOptions,
      dataLoading.transportTypes,
      serviceOptions,
      transportTypes,
      showErrorToast,
      setLoading,
    ]
  );

  // Complete form submission logic
  const processFormSubmit = useCallback(
    async (
      data: NormalizedLoad,
      options: { bypassDuplicateCheck?: boolean } = {}
    ) => {
      const { bypassDuplicateCheck = false } = options;

      const applySubmitValidationErrors = (
        fieldErrors: ManualFieldError<NormalizedLoad>[]
      ) =>
        applyManualValidationErrors(
          formMethods,
          fieldErrors,
          loadBuildingManualValidationDisplayOptions({
            tmsName: TMS.ThreeG,
            activeTabs,
            setActiveTabs,
            toast,
            afterErrorHandled: () =>
              setLoading((l) => ({ ...l, submitting: false })),
          })
        );

      formMethods.clearErrors();

      if (isCreateMode) {
        if (
          data.customer?.externalTMSID !==
          formMethods.getValues('customer.externalTMSID')
        ) {
          showErrorToast(
            'The customer changed. Please submit the updated form again.'
          );
          return;
        }
        // Also runs for duplicate confirmation. Never use another customer's rules.
        if (
          requirementsState.key !== requirementsKey ||
          requirementsState.status !== 'loaded' ||
          !refNumRequirements
        ) {
          if (requirementsState.status === 'failed') {
            void fetchRefNumRequirements();
            showErrorToast(
              'Could not load customer requirements. Retrying; please submit again once loaded.'
            );
          } else {
            showErrorToast(
              'Customer requirements are loading. Please try again shortly.'
            );
          }
          return;
        }
        const referenceErrors = validateThreeGReferences(
          data,
          refNumRequirements
        );
        if (referenceErrors.length) {
          applySubmitValidationErrors(referenceErrors);
          return;
        }
      }

      const duplicateStops = validateDuplicateStopLocations(
        data.pickup?.externalTMSID,
        data.consignee?.externalTMSID
      );
      if (!duplicateStops.ok) {
        applySubmitValidationErrors([
          { path: 'pickup.externalTMSID', message: duplicateStops.message },
          { path: 'consignee.externalTMSID', message: duplicateStops.message },
        ]);
        return;
      }

      const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
        data.pickup?.readyTime,
        data.consignee?.mustDeliver
      );
      if (!pickupDeliveryDates.ok) {
        applySubmitValidationErrors([
          {
            path: 'consignee.mustDeliver',
            message: pickupDeliveryDates.message,
          },
        ]);
        return;
      }

      if (isCreateMode && builtLoad && !bypassDuplicateCheck) {
        setPendingSubmissionData(data);
        setShowDuplicateWarning(true);
        return; // let the dialog handle the confirmation logic
      }

      if (data.pickup?.apptStartTime && data.pickup?.apptEndTime) {
        if (
          !validateAppointmentTimes(
            'Pickup',
            data.pickup.apptStartTime,
            data.pickup.apptEndTime
          )
        ) {
          setLoading((l) => ({ ...l, submitting: false }));
          return;
        }
      }
      if (data.consignee?.apptStartTime && data.consignee?.apptEndTime) {
        if (
          !validateAppointmentTimes(
            'Consignee',
            data.consignee.apptStartTime,
            data.consignee.apptEndTime
          )
        ) {
          setLoading((l) => ({ ...l, submitting: false }));
          return;
        }
      }

      if (isCreateMode) {
        // LTL requires at least one item; FTL/TL do not (for now)
        const isLTL = data.mode === 'LTL';
        if (isLTL && (!data.commodities || data.commodities.length === 0)) {
          formMethods.setError('commodities', {
            type: 'minLength',
            message: 'Must enter an item',
          });
          showErrorToast('Must enter an item');
          return;
        }

        // Create mode - build new load
        setLoading((l) => ({ ...l, submitting: true }));
        try {
          // Validate and transform service type and transport type
          const validationResult =
            validateAndTransformServiceAndTransport(data);
          if (!validationResult) {
            return; // Error toast already shown by validation function
          }
          const { serviceTypeName, transportTypeLabel } = validationResult;

          // Prepare customer object for API
          const customerForApi = denormalizeDatesForTMSForm(
            tmsName,
            data.customer
          );

          // Ensure refNumberCandidates are always string[] for API (backend rejects object)
          const pickupForApi = denormalizeDatesForTMSForm(tmsName, data.pickup);
          const consigneeForApi = denormalizeDatesForTMSForm(
            tmsName,
            data.consignee
          );

          const loadForApi: CreateLoadRequest['load'] =
            omitEmptyThreeGLTLDeliveryDate({
              tmsID: tmsID ?? 0,
              mode: data.mode,
              poNums: data.poNums,
              customer: {
                ...customerForApi,
                refNumberCandidates: normalizeToStringArray(
                  customerForApi?.refNumberCandidates
                ),
              },
              specifications: {
                ...data.specifications,
                serviceType: serviceTypeName,
                transportType: transportTypeLabel,
              },
              pickup: {
                ...pickupForApi,
                refNumberCandidates: normalizeToStringArray(
                  pickupForApi?.refNumberCandidates
                ),
              },
              consignee: {
                ...consigneeForApi,
                refNumberCandidates: normalizeToStringArray(
                  consigneeForApi?.refNumberCandidates
                ),
              },
              rateData: denormalizeDatesForTMSForm(tmsName, data.rateData),
              accessorials: data.accessorials ?? [],
              commodities: (data.commodities ?? []).map((c) => {
                const normalized = normalizeThreeGCommodityForCreate(c);
                const packagingType = normalized?.packagingType ?? '';
                const huTypeId =
                  packagingType === ''
                    ? undefined
                    : Number(packagingType) || undefined;
                const huTypeName =
                  huTypeId != null
                    ? HANDLING_UNIT_TYPES_THREEG.find(
                        (o) => o.value === String(huTypeId)
                      )?.label
                    : undefined;
                const dimensionsUnit =
                  normalized?.dimensionUnit ??
                  (normalized as Record<string, unknown>)?.dimensionsUnit ??
                  'inches';
                return {
                  ...normalized,
                  dimensionsUnit,
                  ...(huTypeId != null && { handlingUnitTypeId: huTypeId }),
                  ...(huTypeName != null && {
                    handlingUnitTypeName: huTypeName,
                  }),
                };
              }),
              additionalReferences: (data.additionalReferences || [])
                .filter(
                  (ref) =>
                    ref.number &&
                    ref.number.trim() &&
                    ref.qualifierId !== undefined &&
                    ref.qualifierId !== null &&
                    ref.qualifierId > 0
                )
                .map((ref) => ({
                  qualifier: ref.qualifier || '',
                  qualifierId: ref.qualifierId,
                  number: ref.number,
                  weight: ref.weight || 0,
                  pieces: ref.pieces || 0,
                  shouldSendToDriver: ref.shouldSendToDriver || false,
                })),
            } as unknown as ThreeGCreatePayloadInput);

          const reqData = withLoadBuildingSuggestionContext(
            {
              clickedSuggestion,
              threadId: threadId ?? '',
              emailId: emailId ?? 0,
              emailExternalId: threadItemId ?? '',
            },
            {
              load: loadForApi,
              // LoadDateInput stores ThreeG date-only selections as canonical YYYY-MM-DD strings.
              metadata: createLoadDateOnlyMetadata(
                data.pickup.readyTime,
                data.consignee.mustDeliver
              ),
            }
          );

          const res = await guardedCreateLoad(
            data.customer,
            reqData,
            customers
          );
          if (!res) {
            setLoading((l) => ({ ...l, submitting: false }));
            return;
          }

          if (!res.isOk()) {
            showErrorToast(res.error.message || 'Error building load.');
            captureResponseInPosthog(
              posthog,
              true,
              getPostHogAnalyticsConfig(isCreateMode)
            );
            setLoading((l) => ({ ...l, submitting: false }));
            return;
          }

          setBuiltLoad(res.value.load);
          FormStorageService.clearFormState(`${tmsName}_${threadId}`);

          setCurrentState((prevState) => {
            if (clickedSuggestion) {
              return {
                ...prevState,
                ...applyCompleteKitSuggestions(
                  prevState,
                  clickedSuggestion.id,
                  SuggestionPipelines.LoadBuilding
                ),
              };
            }
            return prevState;
          });
          captureResponseInPosthog(
            posthog,
            false,
            getPostHogAnalyticsConfig(isCreateMode)
          );
        } catch {
          showErrorToast('Error building load.');
        }
        setLoading((l) => ({ ...l, submitting: false }));
      } else {
        // Edit mode - update existing load
        setLoading((l) => ({ ...l, submitting: true }));
        try {
          const denormalizedData = denormalizeDatesForTMSForm(tmsName, data);

          // Validate and transform service type and transport type
          const validationResult =
            validateAndTransformServiceAndTransport(data);
          if (!validationResult) {
            return; // Error toast already shown by validation function
          }
          const { serviceTypeName, transportTypeLabel } = validationResult;

          // Ensure refNumberCandidates are always string[] for API (backend rejects object)
          const updateData: UpdateTMSLoadPayload =
            omitEmptyThreeGLTLDeliveryDate({
              ...denormalizedData,
              operator: data.operator || '',
              freightTrackingID: data.freightTrackingID || '',
              customer: {
                ...denormalizedData.customer,
                refNumberCandidates: normalizeToStringArray(
                  denormalizedData.customer?.refNumberCandidates
                ),
              },
              pickup: {
                ...denormalizedData.pickup,
                refNumberCandidates: normalizeToStringArray(
                  denormalizedData.pickup?.refNumberCandidates
                ),
              },
              consignee: {
                ...denormalizedData.consignee,
                refNumberCandidates: normalizeToStringArray(
                  denormalizedData.consignee?.refNumberCandidates
                ),
              },
              // Driver / truck / trailer only — omit carrier times from
              // ThreeGTwoStopFields (expectedPickupTime, pickupStart, etc.).
              carrier: {
                firstDriverName: data.carrier?.firstDriverName ?? '',
                firstDriverPhone: data.carrier?.firstDriverPhone ?? '',
                truckNumber: data.carrier?.truckNumber ?? '',
                trailerNumber: data.carrier?.trailerNumber ?? '',
              },
              specifications: {
                ...denormalizedData.specifications,
                serviceType: serviceTypeName,
                transportType: transportTypeLabel,
              },
            } as unknown as ThreeGUpdatePayloadInput);

          if (!load?.ID) {
            showErrorToast('Load ID is required for updates.');
            setLoading((l) => ({ ...l, submitting: false }));
            return;
          }

          if (!carrierIsQualified) {
            showErrorToast(
              data.carrier?.name + ' is not qualified for this load.'
            );
            setLoading((l) => ({ ...l, submitting: false }));
            return;
          }

          const res = await updateTMS(load.ID, {
            operation: TMSOperation.UpdateLoad,
            load: updateData,
          });

          if (!res.isOk()) {
            showErrorToast(res.error.message || 'Error updating load.');
            captureResponseInPosthog(
              posthog,
              true,
              getPostHogAnalyticsConfig(isCreateMode)
            );
            setLoading((l) => ({ ...l, submitting: false }));
            return;
          }

          toast(createSuccessToast('Load updated successfully!'));
          captureResponseInPosthog(
            posthog,
            false,
            getPostHogAnalyticsConfig(isCreateMode)
          );
          await acceptLoadViewSuggestion(
            clickedSuggestion ?? null,
            updateData as Load,
            setCurrentState,
            undefined,
            tmsName
          );
        } catch {
          showErrorToast('Error updating load.');
        }
        setLoading((l) => ({ ...l, submitting: false }));
      }

      // Call custom submit handler if provided
      if (onSubmit) {
        onSubmit(data);
      }
    },
    [
      activeTabs,
      setActiveTabs,
      isCreateMode,
      builtLoad,
      formMethods,
      validateAppointmentTimes,
      validateAndTransformServiceAndTransport,
      showErrorToast,
      tmsName,
      clickedSuggestion,
      tmsID,
      threadId,
      setCurrentState,
      toast,
      createSuccessToast,
      posthog,
      getPostHogAnalyticsConfig,
      load,
      carrierIsQualified,
      onSubmit,
      customers,
      emailId,
      guardedCreateLoad,
      threadItemId,
      fetchRefNumRequirements,
      requirementsKey,
      requirementsState,
      refNumRequirements,
    ]
  );

  const handleFormSubmit = useCallback(
    async (data: NormalizedLoad) => {
      await processFormSubmit(data);
    },
    [processFormSubmit]
  );

  // Handle confirmed submission after user accepts duplicate load warning
  const handleConfirmedSubmit = useCallback(async () => {
    const data = pendingSubmissionData ?? formMethods.getValues();
    await processFormSubmit(data, { bypassDuplicateCheck: true });
    setShowDuplicateWarning(false);
    setPendingSubmissionData(null);
  }, [pendingSubmissionData, formMethods, processFormSubmit]);

  // Default onInvalid handler if none provided
  const defaultOnInvalid: SubmitErrorHandler<NormalizedLoad> = useCallback(
    (errors) => {
      handleFormErrors(errors, {
        tmsName: TMS.ThreeG,
        maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
        activeTabs,
        setActiveTabs,
        toast,
      });
    },
    [activeTabs, setActiveTabs, toast]
  );

  // Use refs to store latest callbacks for handleSubmit wrapper
  // This prevents handleFormSubmitWrapper from changing unnecessarily
  const handleFormSubmitRef = useRef(handleFormSubmit);
  const onInvalidRef = useRef(onInvalid || defaultOnInvalid);

  useEffect(() => {
    handleFormSubmitRef.current = handleFormSubmit;
  }, [handleFormSubmit]);

  useEffect(() => {
    onInvalidRef.current = onInvalid || defaultOnInvalid;
  }, [onInvalid, defaultOnInvalid]);

  // Create a stable wrapper that always calls the latest callbacks
  const handleFormSubmitWrapper = useCallback(
    (e?: React.BaseSyntheticEvent) => {
      return formMethods.handleSubmit(
        handleFormSubmitRef.current,
        onInvalidRef.current
      )(e);
    },
    [formMethods] // Only depend on formMethods, which is stable from useForm
  );

  // Memoize setLocations action to prevent unnecessary re-renders
  const setLocationsAction = React.useCallback<
    React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>
  >(
    (locations) => {
      // Update dataLocations via useThreeGData action
      // This is safe because we removed the sync useEffect that caused infinite loops
      // Locations are merged declaratively in mergedLocations useMemo
      if (typeof locations === 'function') {
        // Handle function form of setState
        setDataLocations((prev) => {
          const next = locations(prev);
          // Only update if content actually changed (prevents unnecessary re-renders)
          if (isEqual(next, prev)) {
            // Preserve null to distinguish "not yet loaded" from "loaded but empty"
            return prev;
          }
          return next ?? [];
        });
      } else {
        const locationsArray = locations ?? [];
        // Only update if content actually changed (prevents unnecessary re-renders)
        setDataLocations((prev) => {
          if (isEqual(locationsArray, prev)) {
            // Preserve null to distinguish "not yet loaded" from "loaded but empty"
            return prev;
          }
          return locationsArray;
        });
      }
    },
    [setDataLocations]
  );

  // CRITICAL: Memoize state and actions objects to prevent unnecessary re-renders
  // This ensures child components only re-render when their specific props change
  const state = useMemo(() => {
    return {
      activeTabs,
      setActiveTabs,
      allTabs,
      expandAll,
      collapseAll,
      defaultView,
      setDefaultView,
      isLoading,
      customers,
      locations: mergedLocations,
      transportTypes,
      serviceOptions,
      ltlAccessorials,
      refNumRequirements,
      showAIHints,
      showDuplicateWarning,
      builtLoad,
      customerID: hasValidCustomerID ? (customerID as number) : undefined,
      customerExternalTmsId: trimmedCustomerExternalTmsId || undefined,
      enableAutoFreightClass: Boolean(selectedCustomer?.enableAutoFreightClass),
    };
  }, [
    activeTabs,
    setActiveTabs,
    allTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
    isLoading,
    customers,
    mergedLocations,
    transportTypes,
    serviceOptions,
    ltlAccessorials,
    refNumRequirements,
    showAIHints,
    showDuplicateWarning,
    builtLoad,
    hasValidCustomerID,
    customerID,
    trimmedCustomerExternalTmsId,
    selectedCustomer?.enableAutoFreightClass,
  ]);

  const actions = useMemo(() => {
    return {
      handleFormSubmit: handleFormSubmitWrapper,
      handleConfirmedSubmit,
      handleClearForm,
      refreshLocations,
      refreshTransportTypes,
      refreshServiceOptions,
      setShowDuplicateWarning,
      setLocations: setLocationsAction,
      setCustomers,
      setCarrierIsQualified: setCarrierIsQualified,
    };
  }, [
    handleFormSubmitWrapper,
    handleConfirmedSubmit,
    handleClearForm,
    refreshLocations,
    refreshTransportTypes,
    refreshServiceOptions,
    setShowDuplicateWarning,
    setLocationsAction,
    setCustomers,
    setCarrierIsQualified,
  ]);

  return {
    formMethods,
    state,
    actions,
  };
}
